/**
 * @fileoverview The character stream a boxed PDF placement addresses.
 *
 * Purpose:
 * - Give the editor's sample-data overlay the same characters the fill action
 *   writes into the PDF, so what the user positions is what prints.
 *
 * Boxed placements (ABN / ACN / TFN / postcode rows, split date groups) cover
 * one contiguous run of printed cells and select their slice of the value with
 * `charStart` + `charBoxes`. Those indices are defined over the *compacted*
 * value, so both sides must compact it identically.
 *
 * Used by:
 * - `src/components/settings/precedents/PdfFieldEditor.tsx` (preview overlay)
 * - mirrors `convex/jobs/precedents/fillPdfAction.ts` (the real fill)
 */

import {
  boxedDateDigits,
  dateBoxDigits,
} from "~/convex/jobs/precedents/fillPdfCore";

/**
 * Compact a value into the stream its boxed placements index into.
 *
 * Date fields reduce to their day-month-year digits — the fill action formats
 * to DD/MM/YYYY and strips the separators, so a year group at `charStart` 6
 * lands on the year rather than on a slash. Every other field type only loses
 * its whitespace, so `12 345 678 901` still runs one digit per cell.
 *
 * @example
 * boxedCharStream("17/08/2026", "date"); // "17082026"
 * boxedCharStream("2026-08-17", "date"); // "17082026"
 * boxedCharStream("12 345 678 901");     // "12345678901"
 */
export function boxedCharStream(
  value: string,
  fieldType?: string,
  placement?: { charStart?: number; charBoxes?: number },
): string {
  if (fieldType === "date") {
    // The fill's own digit stream (MOR-2342): strict day-first / ISO parsing,
    // UTC digits for a timestamp, and an invalid entry's own digits unchanged
    // — so what the editor shows in the cells is exactly what prints.
    return dateBoxDigits(value);
  }
  // Same rule as the fill (MOR-2342 BKY-08): an untyped field holding a
  // complete date, split into cells addressing ≤ 8 digits, streams digits.
  if (placement) {
    const digits = boxedDateDigits(value, placement);
    if (digits) return digits;
  }
  return value.replace(/\s+/g, "");
}
